import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { Toaster, toast } from "sonner";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { useEffect, useRef, lazy, Suspense, useState } from "react";
import axios from "axios";
import { API, installAuthRedirect } from "@/lib/api";
import { useLanguageSuggestion } from "@/hooks/useLanguageSuggestion";
import { useTranslation } from "react-i18next";
import { ChevronDown } from "lucide-react";

// ── Critical path: eagerly loaded (public, first-render pages) ───────────────
import TipPage from "@/pages/TipPage";
import SuccessPage from "@/pages/SuccessPage";
import LoginPage from "@/pages/LoginPage";
import RegisterPage from "@/pages/RegisterPage";
import HomePage from "@/pages/HomePage";
import AuthCallback from "@/pages/AuthCallback";

// ── Secondary public pages ───────────────────────────────────────────────────
const ChooseRolePage = lazy(() => import("@/pages/ChooseRolePage"));
const GoogleReferralPage = lazy(() => import("@/pages/GoogleReferralPage"));
const AvisoLegal = lazy(() => import("@/pages/AvisoLegal"));
const PoliticaPrivacidad = lazy(() => import("@/pages/PoliticaPrivacidad"));
const PoliticaCookies = lazy(() => import("@/pages/PoliticaCookies"));
const AccessDeniedPage = lazy(() => import("@/pages/AccessDeniedPage"));
const PayLinkPage = lazy(() => import("@/pages/PayLinkPage"));

// ── Creator routes (lazy — only loaded when creator logs in) ─────────────────
const ModelDashboard = lazy(() => import("@/pages/ModelDashboard"));
const ModelProfile = lazy(() => import("@/pages/ModelProfile"));
const MyContentPage = lazy(() => import("@/pages/MyContentPage"));
const SubscribersPage = lazy(() => import("@/pages/SubscribersPage"));
const AdminProfileForm = lazy(() => import("@/pages/AdminProfileForm"));

// ── Consumer routes (lazy) ───────────────────────────────────────────────────
const ConsumerDashboard = lazy(() => import("@/pages/ConsumerDashboard"));
const SubscriptionSuccess = lazy(() => import("@/pages/SubscriptionSuccess"));

// ── Shared routes (lazy) ─────────────────────────────────────────────────────
const ServiceChatPage = lazy(() => import("@/pages/ServiceChatPage"));
const ThreadChatPage  = lazy(() => import("@/pages/ThreadChatPage"));
const PrivateChatsPage = lazy(() => import("@/pages/PrivateChatsPage"));
const UserInbox = lazy(() => import("@/pages/UserInbox"));

// ── Admin routes (lazy) ──────────────────────────────────────────────────────
const AdminPanel = lazy(() => import("@/pages/AdminPanel"));

// ── Premium content (lazy — heavy Google Drive + Stripe) ─────────────────────
const GoogleDriveCallbackPage = lazy(() => import("@/pages/GoogleDriveCallbackPage"));
const PremiumServicePage = lazy(() => import("@/pages/PremiumServicePage"));
const PremiumContentPage = lazy(() => import("@/pages/PremiumContentPage"));
const PremiumSuccessPage = lazy(() => import("@/pages/PremiumSuccessPage"));

// ── StarTip 2 routes (lazy) ──────────────────────────────────────────────────
const V2CreatorSetup = lazy(() => import("@/pages/V2CreatorSetup"));
const V2OnboardingComplete = lazy(() => import("@/pages/V2OnboardingComplete"));
const V2CreatorDashboard = lazy(() => import("@/pages/V2CreatorDashboard"));
const V2FanTipping = lazy(() => import("@/pages/V2FanTipping"));
const V2TipSuccess = lazy(() => import("@/pages/V2TipSuccess"));
// ── Canal routes (lazy) ──────────────────────────────────────────────────────
const ChannelPage = lazy(() => import("@/pages/ChannelPage"));
const ChannelInfoPage = lazy(() => import("@/pages/ChannelInfoPage"));
const ChannelSettingsPage = lazy(() => import("@/pages/ChannelSettingsPage"));
const ChannelCreatePage = lazy(() => import("@/pages/ChannelCreatePage"));
const CanalesPage       = lazy(() => import("@/pages/CanalesPage"));
const ModelChannelsPage = lazy(() => import("@/pages/ModelChannelsPage"));
const PayoutIdentityPage = lazy(() => import("@/pages/PayoutIdentityPage"));
const FundraisingReturnPage = lazy(() => import("@/pages/FundraisingReturnPage"));

/**
 * ── PWA INSTALL PROMPT ────────────────────────────────────────────────────────
 * InstallPrompt usa el hook usePwaInstallPrompt (fuente de verdad única).
 * El evento beforeinstallprompt se captura UNA SOLA VEZ a nivel de módulo
 * en hooks/usePwaInstallPrompt.js. Ningún otro componente debe registrar
 * su propio listener para este evento.
 *
 * Para mostrar el install prompt desde cualquier componente:
 *   import { usePwaInstallPrompt } from "@/hooks/usePwaInstallPrompt";
 *   const { canInstallPwa, showInstallPrompt } = usePwaInstallPrompt();
 *   // Llamar showInstallPrompt() SOLO desde un onClick de usuario.
 */
import { InstallPrompt } from "@/components/InstallPrompt";
import { AgeGateModal } from "@/components/AgeGateModal";
import { useAppBadge } from "@/hooks/useAppBadge";
import BroadcastAlertOverlay from "@/components/BroadcastAlertOverlay";
import InviteCodeActivationOverlay from "@/components/InviteCodeActivationOverlay";
import { RewardNotificationOverlay } from "@/components/RewardNotificationOverlay";

// ── Suspense fallback for lazy-loaded routes ──────────────────────────────────
function PageLoader() {
  return (
    <div className="min-h-screen bg-background flex items-center justify-center">
      <div className="animate-pulse text-neon-pink text-xl font-heading font-bold">CARGANDO...</div>
    </div>
  );
}

axios.defaults.withCredentials = true;
// ── Global request timeout ────────────────────────────────────────────────
// Hard cap of 15 s on every HTTP call so axios never hangs forever when the
// backend is overloaded. Individual callsites can still override per-request
// (e.g. file uploads with bigger payloads pass {timeout: 30000}). When this
// timeout fires axios rejects with `err.code === "ECONNABORTED"` and our
// chat polling backoff handles the rest.
axios.defaults.timeout = 15000;

/**
 * ── AXIOS INTERCEPTOR (configuración de cliente) ─────────────────────────────
 * Reescribe peticiones cross-origin al mismo origin del frontend.
 *
 * POR QUÉ ES NECESARIO:
 *   REACT_APP_BACKEND_URL se bake en build-time (ej: creator-tipping-qa.preview.
 *   emergentagent.com). En producción el frontend sirve desde startip.es.
 *   Si el browser enviara cookies a un origin distinto con credentials:include,
 *   CORS lo bloquearía. Este interceptor reescribe la URL para que todas las
 *   peticiones queden en el mismo origin → cookies httpOnly funcionan siempre.
 *
 * IMPLICACIÓN para /api/auth/me:
 *   Tanto en Preview como en Producción, la cookie de sesión se envía
 *   correctamente porque la petición siempre va a window.location.origin.
 *   Si la sesión no existe → el backend devuelve 401 → AuthContext pone
 *   user=null sin romper la app (ver AuthContext.js para el manejo de 401).
 *
 * ENTORNOS:
 *   Preview Emergent → window.location.origin = https://platform-fixes-4.preview.emergentagent.com
 *   Producción       → window.location.origin = https://startip.es
 *   En ambos casos el interceptor funciona sin configuración adicional.
 */
axios.interceptors.request.use((config) => {
  if (config.url && config.url.startsWith("http")) {
    try {
      const url = new URL(config.url);
      if (url.origin !== window.location.origin) {
        config.url = window.location.origin + url.pathname + url.search + url.hash;
      }
    } catch (_) {}
  }
  // Adjuntar el device_id persistente como header para que el backend pueda
  // detectar si dos cuentas distintas comparten el mismo navegador/dispositivo.
  try {
    const { getDeviceId } = require("./utils/deviceId");
    const did = getDeviceId();
    if (did) config.headers["X-Device-ID"] = did;
  } catch (_) {}
  return config;
});

/**
 * ── AXIOS RESPONSE INTERCEPTOR (normalización de errores 422) ───────────────
 * FastAPI devuelve los errores de validación como un ARRAY de objetos:
 *   { detail: [{ type, loc, msg, input, ctx, url }, ...] }
 *
 * El frontend tiene ~140 callsites que hacen `toast.error(err.response?.data?.detail || "…")`
 * o equivalentes. Si `detail` es un array y se pasa directamente a React
 * (toast, setState, JSX), React crashea con "Objects are not valid as a React
 * child" y la app queda inutilizable hasta recargar.
 *
 * Solución de un solo punto: este interceptor aplana `detail` a un string
 * legible ANTES de que el error llegue a cualquier catch. Así todos los
 * callsites existentes siguen funcionando sin cambios.
 */
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    const detail = error?.response?.data?.detail;
    if (Array.isArray(detail)) {
      const flat = detail
        .map((e) => (e && typeof e === "object" ? (e.msg || JSON.stringify(e)) : String(e)))
        .filter(Boolean)
        .join(" · ");
      error.response.data.detail = flat || "Error de validación";
    } else if (detail && typeof detail === "object") {
      error.response.data.detail = detail.msg || JSON.stringify(detail);
    }
    return Promise.reject(error);
  }
);

// ── Global 401 → /login redirect (single source of truth in lib/api.js) ──
// Installs response interceptors on the default `axios` AND on the `api`
// instance. Skips public routes and exempt endpoints (e.g. /auth/me).
installAuthRedirect();

function ActivityPing() {
  const location = useLocation();
  const { user } = useAuth();

  // ── Session-start ping ─────────────────────────────────────────────────────
  // A "session" = first visit OR 30 min of inactivity since last ping.
  // We store a session_id + last-activity timestamp in sessionStorage (per tab).
  // Only on session start do we insert into activity_logs (is_new_session=true).
  // Route changes within the same session never trigger a new session insert.
  useEffect(() => {
    const SESSION_TIMEOUT_MS = 30 * 60 * 1000; // 30 minutes
    const now = Date.now();
    const stored_sid = sessionStorage.getItem("startip_sid");
    const stored_ts  = parseInt(sessionStorage.getItem("startip_last") || "0", 10);

    const isNewSession = !stored_sid || (now - stored_ts) > SESSION_TIMEOUT_MS;

    if (isNewSession) {
      const new_sid = `${now}_${Math.random().toString(36).slice(2, 10)}`;
      sessionStorage.setItem("startip_sid", new_sid);
    }
    // Always update last-activity so 30-min clock resets on navigation
    sessionStorage.setItem("startip_last", String(now));
    // Navigating IS a real interaction.
    sessionStorage.setItem("startip_last_interaction", String(now));

    if (isNewSession) {
      axios.post(
        `${API}/analytics/ping`,
        { session_id: sessionStorage.getItem("startip_sid"), is_new_session: true },
        { withCredentials: true }
      ).catch(() => {});
    }
  }, [location.pathname]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Heartbeat inmediato en navegación real ────────────────────────────────
  // Dispara last_active_at en cuanto el usuario cambia de página (si está logueado).
  // Esto asegura que la "última conexión" visible en chats privados se actualiza
  // al instante, sin esperar el ciclo de 60s del ActivityHeartbeat.
  // Se excluyen páginas de autenticación para evitar ruido.
  const NAV_HEARTBEAT_SKIP = new Set(["/login", "/register", "/auth/session"]);
  useEffect(() => {
    if (!user) return;
    if (NAV_HEARTBEAT_SKIP.has(location.pathname)) return;
    axios
      .post(`${API}/me/heartbeat-active`, {}, { withCredentials: true })
      .catch(() => {});
  }, [location.pathname, user]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Heartbeat every 4 min — gated by real interaction + visibility ────────
  //
  // Why this gate exists: the previous implementation pinged on a plain
  // `setInterval(4 min)`, which inflated active-user counters because it
  // kept firing while:
  //   · the tab was minimized / app was in background, or
  //   · the user had walked away from the device.
  //
  // We now require BOTH:
  //   1. The tab is currently visible (Page Visibility API).
  //   2. There's been at least one real input (mousedown/keydown/touchstart/
  //      scroll) within the last `INTERACTION_WINDOW_MS`.
  //
  // The interaction listeners are registered *passively* once and update a
  // sessionStorage timestamp; this is essentially free in steady state.
  useEffect(() => {
    const HEARTBEAT_MS = 4 * 60 * 1000;       // 4 minutes between attempts
    const INTERACTION_WINDOW_MS = 5 * 60 * 1000;  // must have interacted in last 5 min

    const markInteraction = () => {
      sessionStorage.setItem("startip_last_interaction", String(Date.now()));
    };
    const events = ["mousedown", "keydown", "touchstart", "scroll"];
    events.forEach((ev) => window.addEventListener(ev, markInteraction, { passive: true }));

    const tick = () => {
      const sid = sessionStorage.getItem("startip_sid");
      if (!sid) return;
      // Skip heartbeat when tab is hidden / app is in background.
      if (typeof document !== "undefined" && document.visibilityState !== "visible") return;
      // Skip heartbeat when the user has not interacted recently.
      const last = parseInt(sessionStorage.getItem("startip_last_interaction") || "0", 10);
      if (!last || Date.now() - last > INTERACTION_WINDOW_MS) return;

      axios.post(
        `${API}/analytics/ping`,
        { session_id: sid, is_new_session: false },
        { withCredentials: true }
      ).catch(() => {});
    };

    const timer = setInterval(tick, HEARTBEAT_MS);
    return () => {
      clearInterval(timer);
      events.forEach((ev) => window.removeEventListener(ev, markInteraction));
    };
  }, []);

  // ── Activity heartbeat — updates last_active_at (real interaction only) ─────
  // Fires every 60 s ONLY when the user has genuinely interacted in the last
  // 3 minutes. Does NOT fire during background polling (chat, notifications…)
  // so that `last_active_at` reflects true presence, not app-is-open-in-bg.
  useEffect(() => {
    const ACTIVE_HEARTBEAT_MS = 60 * 1000;   // interval when active
    const IDLE_CUTOFF_MS      = 3 * 60 * 1000; // stop after 3 min idle

    const sendActiveHeartbeat = () => {
      const last = parseInt(sessionStorage.getItem("startip_last_interaction") || "0", 10);
      if (!last || Date.now() - last > IDLE_CUTOFF_MS) return; // idle
      if (document.visibilityState !== "visible") return;      // tab hidden
      axios.post(`${API}/me/heartbeat-active`, {}, { withCredentials: true }).catch(() => {});
    };

    sendActiveHeartbeat(); // send once on mount if recently active
    const timer = setInterval(sendActiveHeartbeat, ACTIVE_HEARTBEAT_MS);
    return () => clearInterval(timer);
  }, []);

  return null;
}

/** Keeps the PWA app icon badge in sync with the unread message count. */
function AppBadgeSync() {
  useAppBadge();
  return null;
}

/**
 * Converts a SPA pathname into a human-readable navigation label for the admin
 * IP logs panel. Uses the URL slug to derive creator/channel/service names
 * (the slug is always human-readable in this app, e.g. "cony-amor-de-compras").
 */
function slugToLabel(slug) {
  // "cony-amor-de-compras" → "Cony amor de compras"
  return slug.replace(/-/g, " ").replace(/^\w/, c => c.toUpperCase());
}

function getNavLabel(pathname) {
  const p = pathname.split("?")[0].replace(/\/$/, "") || "/";
  const parts = p.split("/").filter(Boolean);

  const MAP = {
    "/":                      "Portada",
    "/login":                 "Inicio de sesión",
    "/register":              "Registro",
    "/dashboard":             "Panel creadora",
    "/profile":               "Editar perfil creadora",
    "/mi-contenido":          "Contenido Premium · gestión",
    "/my-services":           "Servicio · mis suscripciones",
    "/private-chats":         "Chats privados",
    "/inbox":                 "Bandeja de mensajes",
    "/admin":                 "Panel admin",
    "/suscriptores":          "Lista de suscriptores",
    "/canales":               "Explorar canales",
    "/mis-canales":           "Mis canales",
    "/crear-canal":           "Crear canal",
    "/cobrar":                "Cobros",
    "/success":               "Pago completado",
    "/subscription-success":  "Suscripción completada",
    "/v2/dashboard":          "Panel V2 creadora",
    "/v2/setup":              "Configurar perfil V2",
  };
  if (MAP[p]) return MAP[p];

  if (p.startsWith("/private-chats/")) return "Chat privado";
  if (p.startsWith("/chat/"))          return "Chat de servicio";
  if (p.startsWith("/thread/"))        return "Chat unificado";
  // Contenido premium — acceso a los archivos de un servicio
  if (p.startsWith("/premium/content/")) return "Contenido Premium · viendo archivos";

  if (p.startsWith("/canal/")) {
    const slug = parts[1] || "";
    const sub  = parts[2];
    if (sub === "settings") return `Ajustes canal: ${slugToLabel(slug)}`;
    if (sub === "info")     return `Info canal: ${slugToLabel(slug)}`;
    return `Canal: ${slugToLabel(slug)}`;
  }
  if (p.startsWith("/tip/")) {
    const slug = parts[1] || "";
    const sub  = parts[2];
    if (sub === "canal")   return `Propina canal · ${slugToLabel(slug)}`;
    if (sub === "premium") return `Servicio · suscripción premium · ${slugToLabel(slug)}`;
    return `Propina · ${slugToLabel(slug)}`;
  }
  if (p.startsWith("/premium/")) {
    const slug = parts[1] || "";
    return `Servicio · página premium: ${slugToLabel(slug)}`;
  }
  if (p.startsWith("/v2/tip/")) {
    const slug = parts[2] || "";
    return `Propina V2 · ${slugToLabel(slug)}`;
  }
  if (p.startsWith("/p/")) return "Enlace de pago directo";
  if (p.startsWith("/admin")) return "Panel admin";

  return p; // fallback: raw path (short, still better than nothing)
}

/**
 * PageTracker: sends a silent POST /api/track-visit on every SPA route change.
 * - Anonymous users: deduped per path per 5 minutes (sessionStorage).
 * - Authenticated users: deduped per path per 90 seconds (sessionStorage) so
 *   the admin panel can see which page each logged-in user is currently on.
 *   The `label` field contains a human-readable page description.
 */
function PageTracker() {
  const location = useLocation();
  const { user, loading } = useAuth();

  useEffect(() => {
    if (loading) return;

    const now      = Date.now();
    const isAuth   = !!user;
    const DEDUP_MS = isAuth ? 90_000 : 5 * 60_000;
    const key      = `pt_${isAuth ? user.user_id : "anon"}_${location.pathname}`;
    const last     = parseInt(sessionStorage.getItem(key) || "0", 10);
    if (now - last < DEDUP_MS) return;
    sessionStorage.setItem(key, String(now));

    const session_id = sessionStorage.getItem("startip_sid") || "";
    const label      = getNavLabel(location.pathname);

    axios.post(
      `${API}/track-visit`,
      { path: location.pathname, session_id, label },
      { withCredentials: true }
    ).catch(() => {});
  }, [location.pathname, user, loading]); // eslint-disable-line react-hooks/exhaustive-deps

  return null;
}

/**
 * GlobalLanguageWidget — selector de idioma retráctil visible en TODAS las páginas.
 *
 * Comportamiento:
 *  - Portada (/): pestaña fija top-right justo bajo el header, sin entrar en el header
 *  - Páginas internas: pestaña retráctil lateral (top-right), se colapsa hacia el borde
 *  - Se excluye de /login y /register (tienen selector propio en su header)
 *  - El estado colapsado se persiste en localStorage
 */
function GlobalLanguageWidget() {
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(() => {
    try { return localStorage.getItem("startip_lang_widget_collapsed") === "true"; } catch { return false; }
  });
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const ref = useRef(null);
  const { i18n } = useTranslation();

  // Cerrar dropdown al clicar fuera — useEffect ANTES de cualquier early return
  useEffect(() => {
    if (!dropdownOpen) return;
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setDropdownOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [dropdownOpen]);

  // Ocultar en login y register
  if (["/login", "/register"].includes(location.pathname)) return null;

  const isHome = location.pathname === "/";

  const toggleCollapse = (e) => {
    e.stopPropagation();
    const next = !collapsed;
    setCollapsed(next);
    if (next) setDropdownOpen(false);
    try { localStorage.setItem("startip_lang_widget_collapsed", String(next)); } catch {}
  };

  const LANGS = [
    { code: "es", label: "Español",    flag: "🇪🇸" },
    { code: "en", label: "English",    flag: "🇬🇧" },
    { code: "fr", label: "Français",   flag: "🇫🇷" },
    { code: "de", label: "Deutsch",    flag: "🇩🇪" },
    { code: "it", label: "Italiano",   flag: "🇮🇹" },
    { code: "pt", label: "Português",  flag: "🇵🇹" },
    { code: "nl", label: "Nederlands", flag: "🇳🇱" },
    { code: "pl", label: "Polski",     flag: "🇵🇱" },
    { code: "ro", label: "Română",     flag: "🇷🇴" },
  ];

  const currentCode = i18n.language?.slice(0, 2) || "es";
  const current = LANGS.find(l => l.code === currentCode) || LANGS[0];

  const select = (code) => {
    i18n.changeLanguage(code);
    localStorage.setItem("startip_lang_manual", "true");
    setDropdownOpen(false);
  };

  return (
    <div
      ref={ref}
      className={`fixed z-[150] flex items-center transition-transform duration-300 ease-in-out ${isHome ? "top-[68px]" : "top-20"} ${collapsed ? "translate-x-[calc(100%-22px)]" : "translate-x-0"} right-0`}
      data-testid="global-lang-widget"
    >
      {/* Pestaña colapso — siempre visible */}
      <button
        onClick={toggleCollapse}
        className="w-[22px] h-9 flex items-center justify-center rounded-l-lg bg-zinc-800/95 border-l border-t border-b border-zinc-700/60 text-zinc-400 hover:text-white text-[10px] shrink-0 transition-colors"
        title={collapsed ? "Mostrar idioma" : "Ocultar"}
        aria-label={collapsed ? "Mostrar selector de idioma" : "Ocultar selector de idioma"}
      >
        {collapsed ? "◂" : "▸"}
      </button>

      {/* Selector (se oculta con translateX) */}
      <div className="relative">
        <button
          onClick={() => setDropdownOpen(o => !o)}
          className="flex items-center gap-1.5 px-2.5 py-2 bg-zinc-900/95 border-t border-b border-l border-zinc-700/60 text-white text-xs font-heading font-bold uppercase tracking-wide whitespace-nowrap backdrop-blur-sm hover:bg-zinc-800/95 transition-colors"
          aria-label="Cambiar idioma"
          data-testid="lang-selector-btn"
        >
          <span className="text-sm leading-none" aria-hidden="true">{current.flag}</span>
          <span>{current.code.toUpperCase()}</span>
          <ChevronDown className={`w-3 h-3 text-zinc-400 transition-transform duration-200 ${dropdownOpen ? "rotate-180" : ""}`} />
        </button>

        {dropdownOpen && (
          <div className="absolute top-[calc(100%+4px)] right-0 w-44 bg-zinc-950 border border-zinc-700/80 rounded-xl shadow-2xl z-[200] overflow-hidden py-1">
            {LANGS.map(({ code, label, flag }) => (
              <button
                key={code}
                onClick={() => select(code)}
                className={`w-full flex items-center gap-2.5 px-3 py-2.5 text-xs font-body transition-colors hover:bg-zinc-800 text-left ${current.code === code ? "text-neon-pink font-bold bg-zinc-900" : "text-zinc-300"}`}
                data-testid={`lang-option-${code}`}
              >
                <span className="text-base leading-none shrink-0" aria-hidden="true">{flag}</span>
                <span className="flex-1">{label}</span>
                {current.code === code && <span className="w-1.5 h-1.5 rounded-full bg-neon-pink shrink-0" />}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function ProtectedRoute({ children, creatorOnly = false, consumerOnly = false }) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-pulse text-neon-pink text-xl font-heading font-bold">CARGANDO...</div>
      </div>
    );
  }
  if (!user) return <Navigate to="/login" />;
  // Google users who haven't chosen a role yet: force role selection
  if (user.role === "pending") return <Navigate to="/choose-role" replace />;
  // Redirect consumers away from creator routes and vice-versa
  const isConsumer = user.role === "consumer";
  const isCreator = user.role === "creator" || user.role === "model";
  if (consumerOnly && !isConsumer) return <Navigate to="/dashboard" />;
  if (creatorOnly && isConsumer) return <Navigate to="/my-services" />;
  return children;
}

function AdminRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-pulse text-neon-pink text-xl font-heading font-bold">CARGANDO...</div>
      </div>
    );
  }
  if (!user || user.role !== "admin") return <Navigate to="/login" />;
  return children;
}

/**
 * useCountdown — devuelve { d, h, m } actualizados cada minuto.
 * Retorna null si no hay fecha o si ya expiró.
 */
function useCountdown(expiresAt) {
  const calc = () => {
    if (!expiresAt) return null;
    const ms = new Date(expiresAt) - new Date();
    if (ms <= 0) return null;
    const total = Math.floor(ms / 1000);
    return {
      d: Math.floor(total / 86400),
      h: Math.floor((total % 86400) / 3600),
      m: Math.floor((total % 3600) / 60),
    };
  };
  const [cd, setCd] = useState(calc);
  useEffect(() => {
    if (!expiresAt) return;
    setCd(calc());
    const id = setInterval(() => setCd(calc()), 60000);
    return () => clearInterval(id);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expiresAt]);
  return cd;
}

/**
 * CommissionPromoBanner — banner compacto y descartable, posicionado en la parte
 * inferior para no bloquear la navegación superior. Solo para role=consumer con promo activa.
 */
function CommissionPromoBanner() {
  const { user } = useAuth();
  const location = useLocation();
  const [feeInfo, setFeeInfo] = useState(null);
  const [dismissed, setDismissed] = useState(false);
  const cd = useCountdown(feeInfo?.expires_at);

  useEffect(() => {
    if (!user || user.role !== "consumer") return;
    const key = `promo_dismissed_${user.user_id}`;
    if (sessionStorage.getItem(key)) { setDismissed(true); return; }
    axios.get(`${API}/me/commission`, { withCredentials: true })
      .then(res => { if (res.data?.is_custom) setFeeInfo(res.data); })
      .catch(() => {});
  }, [user]);

  const handleDismiss = () => {
    setDismissed(true);
    if (user) sessionStorage.setItem(`promo_dismissed_${user.user_id}`, "1");
  };

  const hidden = ["/login", "/register", "/choose-role"].includes(location.pathname);
  if (!feeInfo || dismissed || hidden) return null;

  const cdLabel = cd
    ? (cd.d > 0 ? `${cd.d}d ${cd.h}h ${cd.m}m` : `${cd.h}h ${cd.m}m`)
    : null;

  return (
    <div
      data-testid="global-commission-promo-banner"
      className="fixed top-0 left-0 right-0 z-[9999] flex items-center justify-between gap-2 px-4 py-1.5"
      style={{ background: "linear-gradient(90deg,#005fff 0%,#00c4ff 100%)", boxShadow: "0 2px 12px rgba(0,100,255,0.45)" }}
    >
      <span style={{ fontSize: 13, color: "#fff", fontWeight: 700, lineHeight: 1.3 }}>
        ★ ¡Comisión especial {feeInfo.pct}%!
        {cdLabel && (
          <span style={{ fontWeight: 400, opacity: 0.9, marginLeft: 5 }}>· {cdLabel}</span>
        )}
      </span>
      <button
        data-testid="global-commission-promo-dismiss"
        onClick={handleDismiss}
        className="flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-white text-xs font-bold"
        style={{ background: "rgba(255,255,255,0.2)" }}
        aria-label="Cerrar"
      >×</button>
    </div>
  );
}

function AppRouter() {
  const location = useLocation();
  const { user } = useAuth();

  // ── Sugerencia de idioma basada en IP (una sola vez por dispositivo) ───────
  useLanguageSuggestion();

  // ── iOS visual viewport height fix ────────────────────────────────────────
  // iOS Safari uses the "layout viewport" for `bottom:0` / `100vh`, which
  // extends BEHIND the browser chrome (nav bar). The visual viewport excludes
  // that chrome. We sync --app-height to window.visualViewport.height so that
  // full-screen fixed containers (ChannelPage, etc.) fit the truly visible area.
  // On Android and in PWA standalone mode this is a no-op (vp.height ≈ innerHeight).
  useEffect(() => {
    const update = () => {
      const h = window.visualViewport?.height ?? window.innerHeight;
      document.documentElement.style.setProperty('--app-height', `${h}px`);
    };
    update();
    window.visualViewport?.addEventListener('resize', update);
    window.addEventListener('resize', update);
    return () => {
      window.visualViewport?.removeEventListener('resize', update);
      window.removeEventListener('resize', update);
    };
  }, []);

  // SW update toast removed — silent background updates.

  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <>
      <ActivityPing />
      <AppBadgeSync />
      <PageTracker />
      <CommissionPromoBanner />
      {/* Age gate — renders on ALL routes; shows blocking modal on first visit */}
      <AgeGateModal />
      {/* Intrusive broadcast overlay — shows unread STARTIP notices to non-admin users */}
      <BroadcastAlertOverlay />
      {/* Recompensas internas — ventana emergente para modelos */}
      <RewardNotificationOverlay />
      {/* Blocking invite-code activation overlay — creator MUST acknowledge before continuing */}
      <InviteCodeActivationOverlay />
      {/* Global language widget — disabled */}
      {/* <GlobalLanguageWidget /> */}
      <Suspense fallback={<PageLoader />}>
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/acceso-denegado" element={<AccessDeniedPage />} />
          <Route path="/tip/:modelId" element={<TipPage />} />
          <Route path="/p/:payCode" element={<PayLinkPage />} />
          <Route path="/success" element={<SuccessPage />} />
          <Route path="/fundraising-return" element={<FundraisingReturnPage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/choose-role" element={<ChooseRolePage />} />
          <Route path="/google-referral" element={<GoogleReferralPage />} />
          <Route path="/aviso-legal" element={<AvisoLegal />} />
          <Route path="/privacidad" element={<PoliticaPrivacidad />} />
          <Route path="/cookies" element={<PoliticaCookies />} />

          {/* Creator routes */}
          <Route path="/dashboard" element={<ProtectedRoute creatorOnly><ModelDashboard /></ProtectedRoute>} />
          <Route path="/cobrar" element={<ProtectedRoute creatorOnly><PayoutIdentityPage /></ProtectedRoute>} />
          <Route path="/profile" element={<ProtectedRoute creatorOnly><ModelProfile /></ProtectedRoute>} />
          <Route path="/mi-contenido" element={<ProtectedRoute creatorOnly><MyContentPage /></ProtectedRoute>} />
          <Route path="/suscriptores" element={<ProtectedRoute creatorOnly><SubscribersPage /></ProtectedRoute>} />

          {/* Admin profile (simplified — no creator/payment blocks) */}
          <Route path="/admin/profile" element={<AdminRoute><AdminProfileForm /></AdminRoute>} />

          {/* Consumer routes */}
          <Route path="/my-services" element={<ProtectedRoute><ConsumerDashboard /></ProtectedRoute>} />
          <Route path="/subscription-success" element={<ProtectedRoute><SubscriptionSuccess /></ProtectedRoute>} />

          {/* Shared: chat (both creator and consumer) */}
          <Route path="/chat/:chatId"   element={<ProtectedRoute><ServiceChatPage /></ProtectedRoute>} />
          <Route path="/thread/:threadId" element={<ProtectedRoute><ThreadChatPage /></ProtectedRoute>} />
          <Route path="/private-chats" element={<ProtectedRoute><PrivateChatsPage /></ProtectedRoute>} />
          <Route path="/private-chats/:chatId" element={<ProtectedRoute><PrivateChatsPage /></ProtectedRoute>} />

          {/* Shared: STARTIP official announcements inbox */}
          <Route path="/inbox" element={<ProtectedRoute><UserInbox /></ProtectedRoute>} />

          {/* Admin */}
          <Route path="/admin" element={<AdminRoute><AdminPanel /></AdminRoute>} />

          {/* Premium Content — public + protected */}
          <Route path="/google-drive/callback" element={<GoogleDriveCallbackPage />} />
          <Route path="/premium/success" element={<ProtectedRoute><PremiumSuccessPage /></ProtectedRoute>} />
          <Route path="/premium/content/:serviceId" element={<ProtectedRoute><PremiumContentPage /></ProtectedRoute>} />
          <Route path="/premium/:slug" element={<PremiumServicePage />} />

          {/* Canales de modelos */}
          <Route path="/canal/:channelId" element={<ChannelPage />} />
          <Route path="/canal/:channelId/info" element={<ChannelInfoPage />} />
          <Route path="/canal/:channelId/settings" element={<ProtectedRoute><ChannelSettingsPage /></ProtectedRoute>} />
          <Route path="/crear-canal" element={<ProtectedRoute creatorOnly><ChannelCreatePage /></ProtectedRoute>} />
          <Route path="/mis-canales" element={<ProtectedRoute creatorOnly><ModelChannelsPage /></ProtectedRoute>} />
          <Route path="/canales" element={<ProtectedRoute><CanalesPage /></ProtectedRoute>} />

          {/* StarTip 2 routes */}
          <Route path="/v2/setup" element={<ProtectedRoute><V2CreatorSetup /></ProtectedRoute>} />
          <Route path="/v2/onboarding/complete" element={<ProtectedRoute><V2OnboardingComplete /></ProtectedRoute>} />
          <Route path="/v2/dashboard" element={<ProtectedRoute><V2CreatorDashboard /></ProtectedRoute>} />
          <Route path="/v2/tip/success" element={<V2TipSuccess />} />
          <Route path="/v2/tip/:creatorId" element={<V2FanTipping />} />
        </Routes>
      </Suspense>
    </>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <AppRouter />
        </AuthProvider>
      </BrowserRouter>
      <Toaster position="top-center" richColors theme="dark" />
      <InstallPrompt />
    </div>
  );
}

export default App;
